// An amount in the company currency (`CompanySettings.money`: "$900", "$1,250.50"; compact "$12K" for tiles and
// column headers), with tabular digits so columns of values line up.

import type { ReactNode } from 'react';
import { useSettings } from '@/data/hooks';
import { formatCompactMoney, formatMoney } from '@/domain/format';
import { cn } from '@/ui';

export interface MoneyTextProps {
  value: number;
  /** ISO code; defaults to `settings.currencyCode` (read from the synced company settings). */
  currencyCode?: string;
  /** "$12K" / "$3.4M"; the exact amount goes in the tooltip. */
  compact?: boolean;
  /** What to render for 0 (and NaN), e.g. "—" in tables; omitted renders "$0". */
  zero?: ReactNode;
  className?: string;
}

export function MoneyText({ value, currencyCode, compact = false, zero, className }: MoneyTextProps) {
  const settings = useSettings();
  const code = currencyCode ?? settings.currencyCode;
  if (zero !== undefined && (!Number.isFinite(value) || value === 0)) {
    return <span className={cn('text-fg-subtle', className)}>{zero}</span>;
  }
  const full = formatMoney(value, code);
  const text = compact ? formatCompactMoney(value, code) : full;
  return (
    <span className={cn('whitespace-nowrap tabular-nums', className)} title={text !== full ? full : undefined}>
      {text}
    </span>
  );
}
